import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGBarChart } from '../../components/primitives/PGChart';
import {
  getBranch, peersOf, FEEDBACK, ISO_EVENTS,
} from '../../utils/orgData';
import { PG } from '../../utils/theme';

const {
  FiHeart, FiDollarSign, FiClock, FiCheckSquare, FiUsers,
  FiX, FiStar, FiAlertCircle, FiArrowLeft, FiZap,
} = FiIcons;

const INSIGHTS = {
  nps: {
    title: 'NPS_Insight',
    tone: 'ok',
    accent: PG.emerald,
    summary: 'NPS is tracking above peer-group average, with detractor volume down 18% month-on-month.',
    drivers: [
      'Fitment speed — mentioned positively in 42% of 9–10 scores.',
      'Insurer handling — "barely lifted a finger" is a recurring phrase.',
      'Technician professionalism — top-quartile vs peer group.',
    ],
    actions: [
      'Lock in current shift pattern for another 4 weeks.',
      'Share detractor root-cause with regional manager weekly.',
    ],
  },
  profit: {
    title: 'Profit_Insight',
    tone: 'warn',
    accent: PG.amber,
    summary: 'GP% is 1.4 pts below peer median. Cost-per-job is the primary drag; consumables have drifted upward.',
    drivers: [
      'Urethane consumption per job is 6% above spec.',
      'Overtime hours elevated (+12% vs peer group).',
      'Avg job value slightly below peer set (insurer mix skew).',
    ],
    actions: [
      'Tighten urethane dispense audit — target 2 week recovery.',
      'Rebalance shift rosters to reduce overtime.',
    ],
  },
  turnaround: {
    title: 'Turnaround_Insight',
    tone: 'ok',
    accent: PG.cyan,
    summary: 'Turnaround is inside SLA ceiling and trending down. Curing stage is no longer a bottleneck.',
    drivers: [
      'New pre-stage check added 4 minutes of upstream time — saved 11 downstream.',
      'Fitment bay utilisation at 92%.',
    ],
    actions: [
      'Document the check as a group SOP candidate.',
    ],
  },
  iso: {
    title: 'ISO_Insight',
    tone: 'ok',
    accent: PG.violet,
    summary: 'ISO compliance is strong. One open NCR on primer cure recording — evidence gap only, no product risk.',
    drivers: [
      'Doc control at 100%.',
      'External findings: zero in last 90 days.',
    ],
    actions: [
      'Close primer-cure NCR before month-end.',
    ],
  },
  staff: {
    title: 'Staff_Capacity_Insight',
    tone: 'warn',
    accent: PG.red,
    summary: 'Capacity is adequate today but overtime has been load-bearing for three consecutive weeks.',
    drivers: [
      'Vacancies open: affects Fitment stage bench depth.',
      'Absenteeism slightly above group mean.',
      'Training completion lags group by 6 pts.',
    ],
    actions: [
      'Accelerate the two open hires in Fitment.',
      'Mandatory training refresh this cycle.',
    ],
  },
};

const InsightsSidebar = ({ insightKey, onClose }) => {
  const d = INSIGHTS[insightKey];
  if (!d) return null;
  return (
    <AnimatePresence>
      {insightKey && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed right-0 top-0 h-full w-[440px] bg-[#0A0A0A] border-l border-white/10 z-50 flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: 'easeOut' }}
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 border flex items-center justify-center"
                  style={{ borderColor: d.accent, color: d.accent }}
                >
                  <FiZap />
                </div>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    AI_Analysis
                  </div>
                  <div className="font-doto text-xl text-white">{d.title}</div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center border border-white/10 text-neutral-400 hover:border-[#D71921] hover:text-[#D71921] transition-all"
              >
                <FiX />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-5">
              <div>
                <div className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                  Summary
                </div>
                <p className="font-sans text-[13px] text-neutral-200 leading-relaxed">{d.summary}</p>
              </div>

              <div>
                <div className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                  Drivers
                </div>
                <ul className="space-y-2">
                  {d.drivers.map((x, i) => (
                    <li key={i} className="flex gap-2 text-[12px] text-neutral-300">
                      <span className="font-mono-tech text-[10px] text-neutral-600 pt-0.5">[{String(i + 1).padStart(2, '0')}]</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                  Recommended_Actions
                </div>
                <ul className="space-y-2">
                  {d.actions.map((x, i) => (
                    <li key={i} className="flex gap-2 text-[12px] text-white">
                      <span
                        className="font-mono-tech text-[10px] pt-0.5"
                        style={{ color: d.accent }}
                      >
                        →
                      </span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-white/10 font-mono-tech text-[9px] uppercase tracking-[0.25em] text-neutral-600">
              Insight generated {new Date().toLocaleString('en-GB', { hour12: false })}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const CustomerFeedbackPanel = ({ branchName }) => {
  const own = FEEDBACK.filter((f) => f.branch === branchName);
  const list = own.length ? own : FEEDBACK;
  return (
    <Panel
      title="Customer_Feedback"
      subtitle={own.length ? `${own.length} recent // ${branchName}` : 'Group stream // no branch entries'}
      className="flex-1 min-h-0"
    >
      <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-2">
        {list.map((f, i) => {
          const tone = f.rating >= 9 ? 'ok' : f.rating >= 7 ? 'warn' : 'alert';
          const color = f.rating >= 9 ? PG.emerald : f.rating >= 7 ? PG.amber : PG.red;
          return (
            <div key={i} className="border border-white/5 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <FiStar style={{ color }} />
                  <span className="font-doto text-lg text-white">{f.rating}/10</span>
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">
                    {f.branch}
                  </span>
                </div>
                <StatusPill tone={tone}>{f.ago}</StatusPill>
              </div>
              <p className="font-sans text-[12px] text-neutral-200 leading-snug">"{f.verbatim}"</p>
              <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 mt-1">
                — {f.author}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

const BenchmarkPanel = ({ branch }) => {
  const peers = peersOf(branch);
  const peerAvg = (pick) =>
    peers.length ? +(peers.reduce((a, b) => a + pick(b), 0) / peers.length).toFixed(1) : 0;

  const rows = [
    {
      label: 'SLA_Adherence',
      unit: '%',
      value: branch.ops.slaAdherence,
      peer: peerAvg((b) => b.ops.slaAdherence),
      higherBetter: true,
    },
    {
      label: 'First_Time_Fix',
      unit: '%',
      value: branch.ops.firstTimeFix,
      peer: peerAvg((b) => b.ops.firstTimeFix),
      higherBetter: true,
    },
    {
      label: 'Cost_Per_Job',
      unit: 'R',
      value: branch.finance.costPerJob,
      peer: peerAvg((b) => b.finance.costPerJob),
      higherBetter: false,
    },
    {
      label: 'NPS',
      unit: '/10',
      value: branch.cx.nps,
      peer: peerAvg((b) => b.cx.nps),
      higherBetter: true,
    },
  ];

  return (
    <Panel
      title="Benchmark_vs_Peers"
      subtitle={`Peer group: ${branch.peerGroup} // ${peers.length} branches`}
      className="flex-1 min-h-0"
    >
      <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-3">
        {rows.map((r) => {
          const max = Math.max(r.value, r.peer) * 1.15 || 1;
          const winBranch = r.higherBetter ? r.value >= r.peer : r.value <= r.peer;
          return (
            <div key={r.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400">
                  {r.label}
                </span>
                <StatusPill tone={winBranch ? 'ok' : 'alert'}>
                  {winBranch ? 'Above_Peer' : 'Below_Peer'}
                </StatusPill>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-12 font-mono-tech text-[9px] uppercase text-neutral-600">Branch</div>
                  <div className="flex-1 h-4 bg-white/5 relative">
                    <div
                      className="h-full"
                      style={{
                        width: `${(r.value / max) * 100}%`,
                        background: winBranch ? PG.emerald : PG.red,
                      }}
                    />
                  </div>
                  <div className="w-20 text-right font-doto text-sm text-white">
                    {r.unit === 'R' ? `R${Math.round(r.value).toLocaleString('en-ZA')}` : `${r.value}${r.unit}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-12 font-mono-tech text-[9px] uppercase text-neutral-600">Peer_Avg</div>
                  <div className="flex-1 h-4 bg-white/5 relative">
                    <div
                      className="h-full"
                      style={{
                        width: `${(r.peer / max) * 100}%`,
                        background: PG.neutral500,
                      }}
                    />
                  </div>
                  <div className="w-20 text-right font-doto text-sm text-neutral-400">
                    {r.unit === 'R' ? `R${Math.round(r.peer).toLocaleString('en-ZA')}` : `${r.peer}${r.unit}`}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

const ISOLogPanel = ({ branchName }) => {
  const severityTone = { High: 'alert', Medium: 'warn', Low: 'neutral', Info: 'info' };
  const events = ISO_EVENTS.filter((e) => e.branch === branchName || e.branch === 'Group');
  return (
    <Panel
      title="ISO_Log"
      subtitle={`${events.length} entries // branch + group`}
      className="flex-1 min-h-0"
    >
      <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-2">
        {events.length === 0 ? (
          <div className="text-neutral-600 font-mono-tech text-[10px] uppercase tracking-widest p-2">
            No entries
          </div>
        ) : events.map((e) => (
          <div key={e.id} className="border border-white/5 p-2">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">
                {e.id} // {e.branch}
              </span>
              <StatusPill tone={severityTone[e.severity] || 'neutral'}>{e.severity}</StatusPill>
            </div>
            <div className="font-sans text-[11px] text-white leading-snug">{e.title}</div>
            <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 mt-1">
              {e.type} // {e.status} // {e.age} // {e.owner}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

export default function HolisticBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);
  const [insight, setInsight] = useState(null);

  const dailyInstalls = useMemo(() => {
    const out = [];
    const base = branch ? branch.ops.jobsToday : 30;
    const labels = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      labels.push(`${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`);
      const dayOfWeek = d.getDay();
      const weekend = dayOfWeek === 0 || dayOfWeek === 6 ? 0.55 : 1;
      const noise = 0.85 + Math.random() * 0.3;
      out.push(Math.round(base * weekend * noise));
    }
    return { labels, values: out };
  }, [branch]);

  if (!branch) {
    return (
      <div className="h-full flex items-center justify-center flex-col text-neutral-500">
        <div className="font-doto text-4xl text-white mb-2">Branch_Not_Found</div>
        <Link to="/sliver/holistic" className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#D71921] hover:text-white">
          ← Back to Holistic
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-4 min-h-0">
      {/* Top KPI row */}
      <div className="grid grid-cols-5 gap-4 flex-shrink-0">
        <KPICard
          title="NPS"
          value={branch.cx.nps}
          unit="/10"
          target="9.0"
          trend={branch.cx.nps >= 9 ? 'up' : 'down'}
          trendValue={branch.cx.nps >= 9 ? '+0.4' : '-0.3'}
          icon={FiHeart}
          accent={PG.emerald}
          clickable
          onClick={() => setInsight('nps')}
        />
        <KPICard
          title="Profit"
          value={`${branch.finance.gpPct}%`}
          unit="GP"
          target="≥43%"
          trend={branch.finance.gpPct >= 43 ? 'up' : 'down'}
          trendValue={branch.finance.gpPct >= 43 ? '+0.8%' : '-1.4%'}
          icon={FiDollarSign}
          accent={PG.red}
          clickable
          onClick={() => setInsight('profit')}
        />
        <KPICard
          title="Turnaround"
          value={branch.ops.avgTurnaroundMin}
          unit="min"
          target="≤50"
          trend={branch.ops.avgTurnaroundMin <= 50 ? 'up' : 'down'}
          trendValue={branch.ops.avgTurnaroundMin <= 50 ? '-2m' : '+4m'}
          icon={FiClock}
          accent={PG.cyan}
          clickable
          onClick={() => setInsight('turnaround')}
        />
        <KPICard
          title="ISO_Compliance"
          value={`${branch.iso.complianceScore}%`}
          target="≥99%"
          trend={branch.iso.complianceScore >= 99 ? 'up' : 'down'}
          trendValue={branch.iso.complianceScore >= 99 ? '+0.2%' : '-0.5%'}
          icon={FiCheckSquare}
          accent={PG.violet}
          clickable
          onClick={() => setInsight('iso')}
        />
        <KPICard
          title="Staff_Capacity"
          value={branch.hr.headcount - branch.hr.vacancies}
          unit={`/${branch.hr.headcount}`}
          target="Full"
          trend={branch.hr.vacancies === 0 ? 'up' : 'down'}
          trendValue={branch.hr.vacancies === 0 ? 'Full' : `-${branch.hr.vacancies}`}
          icon={FiUsers}
          accent={PG.amber}
          clickable
          onClick={() => setInsight('staff')}
        />
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Left 7/12 */}
        <div className="col-span-7 flex flex-col gap-4 min-h-0">
          <Panel
            title="Daily_Installations"
            subtitle="Last 30 days // jobs completed"
            right={<StatusPill tone="info">{branch.name}</StatusPill>}
            className="flex-1 min-h-0"
          >
            <div className="h-full p-2">
              <PGLineChart
                categories={dailyInstalls.labels}
                series={[{
                  name: 'Installs',
                  data: dailyInstalls.values,
                  lineStyle: { color: PG.red, width: 2 },
                  itemStyle: { color: PG.red },
                }]}
              />
            </div>
          </Panel>

          <CustomerFeedbackPanel branchName={branch.name} />
        </div>

        {/* Right 5/12 */}
        <div className="col-span-5 flex flex-col gap-4 min-h-0">
          <BenchmarkPanel branch={branch} />
          <ISOLogPanel branchName={branch.name} />
        </div>
      </div>

      <InsightsSidebar insightKey={insight} onClose={() => setInsight(null)} />
    </div>
  );
}
